import { markRaw, ref, computed } from 'vue'
import i18n from '@/i18n'
import { ModalType, initAndRevealMainModal, closeMainModal } from '@/components/modals/modals-util'
import wrenchIcon from '@/assets/popups/wrench-icon.svg'
import settingsMaintenanceIcon from '@/assets/popups/settings-maintenance-icon.svg'
import alertIcon from '@/assets/popups/alert.svg'
import { IModalProps } from '@/store/custom-modal.module'
import MaintenanceScheduled from '@/components/modals/maintenance/MaintenanceScheduled.vue'
import MaintenanceInProgress from '@/components/modals/maintenance/MaintenanceInProgress.vue'
import { isMaintenanceInProgress, isMaintenanceScheduled } from '@/services/utils'
import AlertInfo from '@/components/modals/maintenance/AlertInfo.vue'

export enum MaintenancePopupType {
  Scheduled = 'scheduled',
  InProgress = 'in-progress',
  Info = 'info'
}

const STORAGE_KEY_SCHEDULED = 'maintenance_popup_scheduled_dismissed'
const STORAGE_KEY_IN_PROGRESS = 'maintenance_popup_in_progress_dismissed'
const STORAGE_KEY_INFO = 'info_popup_dismissed'


const scheduledDismissed = ref<boolean>(localStorage.getItem(STORAGE_KEY_SCHEDULED) === 'true')
const inProgressDismissed = ref<boolean>(localStorage.getItem(STORAGE_KEY_IN_PROGRESS) === 'true')
const infoDismissed = ref<boolean>(localStorage.getItem(STORAGE_KEY_INFO) === 'true')

const markAsDismissedScheduledMaintenance = (): void => {
  localStorage.setItem(STORAGE_KEY_SCHEDULED, 'true')
  scheduledDismissed.value = true
}

const markAsDismissedInProgressMaintenance = (): void => {
  localStorage.setItem(STORAGE_KEY_IN_PROGRESS, 'true')
  inProgressDismissed.value = true
}

const markAsDismissedInfoPopup = (): void => {
  localStorage.setItem(STORAGE_KEY_INFO, 'true')
  infoDismissed.value = true
}

export const useMaintenanceModals = () => {
  const shouldShowInProgressPopup = computed(() => {
    const isDismissed = localStorage.getItem(STORAGE_KEY_IN_PROGRESS) === 'true'
    return isMaintenanceInProgress && !isDismissed
  })

  const shouldShowScheduledPopup = computed(() => {
    const isDismissed = localStorage.getItem(STORAGE_KEY_SCHEDULED) === 'true'
    return isMaintenanceScheduled && !isMaintenanceInProgress && !isDismissed
  })

  const initScheduledMaintenancePopup = () => {
    const handleClose = () => {
      markAsDismissedScheduledMaintenance()
      closeMainModal()
    }

    const modalConfig: IModalProps = {
      modalType: ModalType.MAINTENANCE_SCHEDULE_MODAL,
      componentContent: markRaw(MaintenanceScheduled),
      props: {
        headerProps: {
          icon: wrenchIcon,
          text: i18n.global.t('scheduled-maintenance-title'),
        },
        footerProps: {
          buttonCancel: i18n.global.t('got-it-thanks')
        },
        disableClickOutside: true,
        noBlur: true,
        onDismiss: handleClose
      },
    }

    initAndRevealMainModal(modalConfig)
  }

  const initInProgressMaintenancePopup = () => {
    const handleClose = () => {
      markAsDismissedInProgressMaintenance()
      closeMainModal()
    }

    const modalConfig: IModalProps = {
      modalType: ModalType.MAINTENANCE_IN_PROGRESS,
      componentContent: markRaw(MaintenanceInProgress),
      props: {
        headerProps: {
          text: i18n.global.t('in-progress-maintenance-title'),
          icon: settingsMaintenanceIcon
        },
        footerProps: {
          buttonCancel: i18n.global.t('got-it-thanks')
        },
        disableClickOutside: true,
        noBlur: true,
        onDismiss: handleClose
      },
    }

    initAndRevealMainModal(modalConfig)
  }

  const initInfoPopup = () => {
    const handleClose = () => {
      markAsDismissedInfoPopup()
      closeMainModal()
    }

    const modalConfig: IModalProps = {
      modalType: ModalType.INFO,
      componentContent: markRaw(AlertInfo),
      props: {
        headerProps: {
          text: i18n.global.t('alert-info-title'),
          icon: alertIcon
        },
        footerProps: {
          buttonCancel: i18n.global.t('got-it-thanks')
        },
        disableClickOutside: true,
        noBlur: true,
        onDismiss: handleClose
      },
    }

    initAndRevealMainModal(modalConfig)
  }

  const resetDismissedState = (type?: MaintenancePopupType): void => {
    if (!type || type === MaintenancePopupType.Scheduled) {
      localStorage.removeItem(STORAGE_KEY_SCHEDULED)
      scheduledDismissed.value = false
    }
    if (!type || type === MaintenancePopupType.InProgress) {
      localStorage.removeItem(STORAGE_KEY_IN_PROGRESS)
      inProgressDismissed.value = false
    }
    if (!type || type === MaintenancePopupType.Info) {
      localStorage.removeItem(STORAGE_KEY_IN_PROGRESS)
      infoDismissed.value = false
    }
  }

  const maintenancePopupType = computed<MaintenancePopupType | null>(() => {
    if (shouldShowInProgressPopup.value) {
      return MaintenancePopupType.InProgress
    } else if (shouldShowScheduledPopup.value) {
      return MaintenancePopupType.Scheduled
    }

    return null
  })

  const showMaintenancePopupByType = (type: MaintenancePopupType): void => {
    if (type === MaintenancePopupType.InProgress) {
      initInProgressMaintenancePopup()
    } else if (type === MaintenancePopupType.Scheduled) {
      initScheduledMaintenancePopup()
    }
  }

  return {
    initScheduledMaintenancePopup,
    initInProgressMaintenancePopup,
    resetDismissedState,
    showMaintenancePopupByType,
    initInfoPopup,
    maintenancePopupType
  }
}

